import { FormArray, FormControl, FormGroup } from '@angular/forms';
import moment from 'moment';
import { Moment } from 'moment';

import { EditorEligibilityRule } from './editor-eligibility-rule.model';


export class EditorEligibilityRuleHasEventCount extends EditorEligibilityRule {
  className = 'HasEventCount';
  operatorName = 'hasEventCount';

  eventTypeGroups: string[];
  blueprints: { id: string, categories: string[] }[];
  start: Moment;
  end: Moment;
  relativeStart: { years?: number, months?: number, days?: number };
  comparison: string;
  count: number;

  constructor(obj?: any) {
    super({
      id: obj?.id ?? null
    });
    this.eventTypeGroups = obj?.eventTypeGroups ?? [];
    this.blueprints = obj?.blueprints ?? [];
    this.start = obj?.start ?? null;
    this.end = obj?.end ?? null;
    this.relativeStart = obj?.relativeStart ?? null;
    this.comparison = obj?.comparison ?? null;
    this.count = obj?.count ?? null;
  }

  static matchesTypeOfLegacySerializedValues(legacyEligibilityRules: any) {
    return legacyEligibilityRules?.args?.[0]?.args?.[0]?.op === 'fetchFromES'
      && legacyEligibilityRules?.args?.[0]?.args?.[0]?.options?.type === 'event'
      && legacyEligibilityRules?.args?.[1]?.options?.value > 1;
  }

  static getFromLegacySerializedValues(legacyEligibilityRules: any) {
    const keyIds = Object.keys(legacyEligibilityRules.args[0].args[0].options.filter)
      .filter(keyId => keyId.length === 36
        && keyId.charAt(8) === '-'
        && keyId.charAt(13) === '-'
        && keyId.charAt(18) === '-'
        && keyId.charAt(23) === '-'
      );
    const blueprints = keyIds
      .map(keyId => ({
        id: keyId,
        categories: typeof(legacyEligibilityRules.args[0].args[0].options.filter[keyId]) === 'string'
          ? [legacyEligibilityRules.args[0].args[0].options.filter[keyId]]
          : legacyEligibilityRules.args[0].args[0].options.filter[keyId]
      }));
    const relativeAfter = legacyEligibilityRules.args[0].args[0].options.filter.relativeAfter;
    const relativeStart = {
      years: relativeAfter?.years === undefined ? null : relativeAfter.years * -1,
      months: relativeAfter?.months === undefined ? null : relativeAfter.months * -1,
      days: relativeAfter?.days === undefined ? null : relativeAfter.days * -1,
    };

    return new this({
      blueprints,
      start: legacyEligibilityRules.args[0].args[0].options.filter.after === undefined
          || legacyEligibilityRules.args[0].args[0].options.filter.after === null
        ? undefined
        : moment(legacyEligibilityRules.args[0].args[0].options.filter.after),
      end: legacyEligibilityRules.args[0].args[0].options.filter.before === undefined
          || legacyEligibilityRules.args[0].args[0].options.filter.before === null
        ? undefined
        : moment(legacyEligibilityRules.args[0].args[0].options.filter.before),
      relativeStart,
      comparison: legacyEligibilityRules.op,
      count: legacyEligibilityRules.args[1].options.value
    });
  }

  static getFromSerializedValues(serializedValues: any) {
    return new this({
      eventTypeGroups: serializedValues.eventTypeGroups,
      blueprints: serializedValues.blueprints,
      start: serializedValues.start === null ? null : moment(serializedValues.start),
      end: serializedValues.end === null ? null : moment(serializedValues.end),
      relativeStart: serializedValues.relativeStart,
      comparison: serializedValues.comparison,
      count: serializedValues.count
    });
  }

  static getEmptyControlConfig(): { [key: string]: any; } {
    return {
      eventTypeGroups: [{ value: [], disabled: true }],
      blueprints: new FormArray([]),
      start: [{ value: null, disabled: true }],
      end: [{ value: null, disabled: true }],
      relativeStartYears: [{ value: null, disabled: true }],
      relativeStartMonths: [{ value: null, disabled: true }],
      relativeStartDays: [{ value: null, disabled: true }],
      comparison: [{ value: null, disabled: true }],
      count: [{ value: null, disabled: true }]
    };
  }

  createCopy(): EditorEligibilityRuleHasEventCount {
    return new EditorEligibilityRuleHasEventCount({
      id: this.id,
      eventTypeGroups: this.eventTypeGroups,
      blueprints: [this.blueprints.map(
        blueprint => ({ id: blueprint.id, categories: [...blueprint.categories] })
      )],
      start: this.start,
      end: this.end,
      relativeStart: this.relativeStart,
      comparison: this.comparison,
      count: this.count
    });
  }

  getEventTypesAsString(eventTypeGroupNames: { id: string, name: string }[]): string {
    if (this.eventTypeGroups.length === 0) { return ''; }
    return this.eventTypeGroups
      .map(eventTypeGroup =>
        eventTypeGroupNames.find(eventTypeGroupName => eventTypeGroupName.id === eventTypeGroup) ?? { id: eventTypeGroup, name: $localize `Not valid` }
      )
      .map(eventTypeGroupName => eventTypeGroupName.name)
      .join(', ');
  }

  getBlueprintsAsString(
    blueprintNamesWithCategoryNames: { id: string, name: string, categoryNames: { id: string, name: string }[] }[]
  ): string {
    if (this.blueprints.length === 0) { return ''; }
    return this.blueprints
      .map(blueprint => {
          const blueprintNameWithCategoryNames = blueprintNamesWithCategoryNames.find(
            blueprintNameWithCategoryNamesTemp => blueprintNameWithCategoryNamesTemp.id === blueprint.id
          ) ?? { id: blueprint.id, name: $localize `Not valid`, categoryNames: [] };
          return `${blueprintNameWithCategoryNames.name} [${this.getBlueprintCategoriesAsString(
            blueprint.categories,
            blueprintNameWithCategoryNames.categoryNames
          )}]`;
        })
      .join(', ');
  }

  getBlueprintCategoriesAsString(blueprintCategories: string[], categoryNames: { id: string, name: string }[]) {
    return blueprintCategories
      .map(blueprintCategory =>
        categoryNames.find(categoryName => categoryName.id === blueprintCategory) ?? { id: blueprintCategory, name: $localize `Not valid` }
      )
      .map(categoryName => categoryName.name)
      .join(', ');
  }

  getControlsConfig(): { [key: string]: any; } {
    return {
      eventTypeGroups: [this.eventTypeGroups],
      blueprints: new FormArray(this.blueprints.map(blueprint => {
        return new FormGroup({
          id: new FormControl(blueprint.id),
          categories: new FormControl(blueprint.categories)
        });
      })),
      start: [this.start],
      end: [this.end],
      relativeStartYears: [this.relativeStart.years],
      relativeStartMonths: [this.relativeStart.months],
      relativeStartDays: [this.relativeStart.days],
      comparison: [this.comparison],
      count: [this.count]
    };
  }

  getSerializedValues(): any {
    const relativeStart: { years?: number, months?: number, days?: number } = {};
    if (this.relativeStart.years !== null) {
      relativeStart.years = this.relativeStart.years;
    }
    if (this.relativeStart.months !== null) {
      relativeStart.months = this.relativeStart.months;
    }
    if (this.relativeStart.days !== null) {
      relativeStart.days = this.relativeStart.days;
    }

    return {
      eventTypeGroups: this.eventTypeGroups,
      blueprints: this.blueprints,
      start: this.start,
      end: this.end,
      relativeStart,
      comparison: this.comparison,
      count: this.count
    };
  }
}
